/**
 * The company size bands a profile type's "Company size" condition can require (#724).
 *
 * They are the bands the enrichment provider writes into
 * `profiles.company_employee_range`, LinkedIn's own: the base holds "0-1" and "2-10"
 * and no "1-10", so the list follows the data rather than LinkedIn's marketing pages.
 * Bands rather than a number because a band is all the provider gives: whether
 * "51-200" counts for "more than 100" is the admin's call, made by ticking it or not.
 *
 * Shared because both ends need the same list: the API validates a condition's bands
 * against it, the block editor renders one checkbox per band, and the overview's fill
 * rate counts the profiles whose band is one of them.
 */

export const COMPANY_SIZE_BANDS = [
    "0-1",
    "2-10",
    "11-50",
    "51-200",
    "201-500",
    "501-1000",
    "1001-5000",
    "5001-10000",
    "10001+",
] as const;

export type CompanySizeBand = (typeof COMPANY_SIZE_BANDS)[number];

/** The bands as an admin reads them, thousands separated the way LinkedIn prints them. */
export const COMPANY_SIZE_BAND_LABELS: Record<CompanySizeBand, string> = {
    "0-1": "0-1",
    "2-10": "2-10",
    "11-50": "11-50",
    "51-200": "51-200",
    "201-500": "201-500",
    "501-1000": "501-1,000",
    "1001-5000": "1,001-5,000",
    "5001-10000": "5,001-10,000",
    "10001+": "10,001+",
};

/**
 * Every character a stored range keeps once normalised: digits, the dash and the plus.
 *
 * The provider and the CSV import do not agree on spelling ("10,001+", " 51-200 ",
 * "51-200 employees"), and stripping everything else turns each of them into the
 * canonical band. A value the provider sends for "unknown" ("None-None") comes out as
 * "-", which is no band. The match SQL applies the same class with `regexp_replace`:
 * keep the two in step.
 */
export const COMPANY_SIZE_STRIP_PATTERN = "[^0-9+-]";

const BAND_SET: ReadonlySet<string> = new Set(COMPANY_SIZE_BANDS);

/** A stored range as its canonical band, or `null` when it is none of them. */
export function normalizeCompanySizeBand(raw: string | null | undefined): CompanySizeBand | null {
    if (raw == null) return null;
    const stripped = raw.replace(new RegExp(COMPANY_SIZE_STRIP_PATTERN, "g"), "");
    return BAND_SET.has(stripped) ? (stripped as CompanySizeBand) : null;
}
